import AppContext from '@/AppContext';
import { EntityList } from '@/components/EntityList';
import { ListColumn } from '@/components/ListLayout';
import { useAsyncEffect } from '@/hooks';
import { Form, Grid, Pagination } from '@/semantic-ui/components';
import { useStores } from '@/store';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useContext, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toNum } from 'sol-data';
import { TemporaryEmployee } from 'sol-data/dist/entities/temporary-employee';
import PositionFilterDropdown from '../PositionFilterDropdown';

const RESULTS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 200;

interface OnboardingSubmissionListProps {
  status: 'submitted' | 'approved' | 'active';
}

export const OnboardingSubmissionList = observer(({ status }: OnboardingSubmissionListProps) => {
  const { temporaryEmployeeStore } = useStores();
  const [currentPage, setCurrentPage] = useState(1);
  const appContext = useContext(AppContext);
  const isAdmin = appContext?.isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get('page') ? Number(searchParams.get('page')) : 1;
  const positionId = searchParams.get('position')
    ? Number(searchParams.get('position'))
    : undefined;
  const [search, setSearch] = useState('');

  const fetchData = async ({
    pageNumber,
    searchValue,
    positionIdValue,
  }: {
    pageNumber: number;
    searchValue?: string;
    positionIdValue?: number;
  }) => {
    await temporaryEmployeeStore.fetchAllPaginated(
      pageNumber,
      status,
      searchValue,
      positionIdValue,
    );

    setCurrentPage(pageNumber);
  };

  const { isLoading, errorMessage } = temporaryEmployeeStore;

  const columns: ListColumn<TemporaryEmployee>[] = [
    {
      name: 'Name',
      key: 'fullName',
      width: { sm: 3, lg: 3 },
      render: ({ fullName }) => fullName,
    },
    {
      name: 'Email',
      key: 'user.email',
      width: { sm: 4, lg: 4 },
      render: ({ user }) => user?.email,
    },
    {
      name: 'Submitted On',
      key: 'submittedAt',
      width: { sm: 4, lg: 4 },
      render: ({ submittedAt }) =>
        submittedAt ? submittedAt.format('dddd, MMMM Do YYYY [at] h:mmA') : '',
    },
    {
      name: 'Position',
      key: 'position',
      width: { sm: 2, lg: 2 },
      render: ({ position }) => position?.label,
    },
  ];

  useAsyncEffect(async () => {
    await fetchData({ pageNumber: 1, searchValue: '', positionIdValue: undefined });
  }, [status]);

  const handlePositionChange = async (value: number) => {
    setSearch('');

    await changePage(1, '', value);
  };

  const changePage = useCallback(
    async (pageNumber: number, searchValue?: string, positionIdValue?: number) => {
      temporaryEmployeeStore.setPage(pageNumber);

      setSearchParams({
        page: pageNumber.toString(),
        position: positionIdValue?.toString() ?? '',
      });

      await fetchData({ pageNumber, searchValue, positionIdValue });
    },
    [status],
  );

  const updateSearch = useCallback(
    debounce(async (searchValue: string) => {
      await changePage(1, searchValue, undefined);
    }, DEBOUNCE_DELAY),
    [changePage],
  );

  return (
    <>
      <EntityList
        title="Onboarding Submissions"
        searchLabel="Search by name or email"
        columns={columns}
        items={temporaryEmployeeStore.values}
        // @ts-ignore
        titleProp="user.fullName"
        searchValue={search}
        onSearchChange={(filter) => {
          setSearch(filter);
          updateSearch(filter);
        }}
        onDismiss={() => temporaryEmployeeStore.clearError()}
        onRefresh={() => changePage(page, search, positionId)}
        errorMessage={errorMessage}
        isLoading={isLoading}
        link={
          isAdmin
            ? ({ id: temporaryEmployeeId }) =>
                `/onboarding/submissions/details/${temporaryEmployeeId}/personal`
            : undefined
        }
        noNewButton
        searchHeaderContent={
          <Form>
            <Form.Field>
              <PositionFilterDropdown
                limited
                clearable
                name="position"
                placeholder="Filter by position"
                positionId={positionId ? toNum(positionId) : undefined}
                onChange={(_, value) => handlePositionChange(value)}
              />
            </Form.Field>
          </Form>
        }
      />
      <Grid centered>
        <Grid.Row>
          <Pagination
            activePage={currentPage}
            totalPages={Math.ceil(temporaryEmployeeStore.allEmployeesCount / RESULTS_PER_PAGE)}
            onPageChange={(_, { activePage }) => {
              changePage(activePage as number, search, positionId);
            }}
            siblingRange={2}
            boundaryRange={0}
            firstItem={{ content: 'First' }}
            lastItem={{ content: 'Last' }}
            ellipsisItem={null}
            prevItem={null}
            nextItem={null}
          />
        </Grid.Row>
      </Grid>
    </>
  );
});
